import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, Boxes, FileSearch, Workflow, BrainCircuit } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { PRODUCTS, ENGINES, STATS, VERTICALS, IMAGES, MANIFESTO, LOGO_URL, CASE_STUDIES } from "@/data/content";
import NewsletterForm from "@/components/NewsletterForm";

const featured = [PRODUCTS[0], PRODUCTS[1], PRODUCTS[2], PRODUCTS[5]];
const featuredIcons = [BrainCircuit, Workflow, FileSearch, ShieldCheck];

export default function Home() {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="grid-bg relative flex min-h-screen items-end overflow-hidden pt-32 pb-20">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="pointer-events-none absolute inset-0">
          <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-[0.07]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F7F8FA] via-[#F7F8FA]/85 to-[#F7F8FA]/60" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-10 flex flex-col gap-5 sm:flex-row sm:items-center">
            <motion.img
              src={LOGO_URL}
              alt="AIS logo"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="h-28 w-28 shrink-0 object-cover mix-blend-multiply sm:h-36 sm:w-36"
            />
            <h1 data-testid="hero-company-name" className="inline-block font-heading text-4xl font-bold tracking-tight leading-[1.05] text-zinc-900 sm:text-5xl lg:text-6xl">
              <span
                data-testid="hero-augmented"
                aria-label="Augmented"
                className="flex justify-between text-2xl font-bold text-cyan-600 sm:text-3xl lg:text-4xl"
                style={{ fontFamily: "'Orbitron', sans-serif", textShadow: "0 0 24px rgba(0,229,255,0.45)" }}
              >
                {"AUGMENTED".split("").map((c, i) => (
                  <span key={i}>{c}</span>
                ))}
              </span>
              <span className="block font-normal" style={{ fontFamily: "'Quantico', sans-serif" }}>INTELLIGENCE SYSTEMS</span>
            </h1>
          </motion.div>
          <MaskedLines
            lines={["AI that works for you,", <span key="a" className="text-cyan-600">starting this week.</span>]}
            lineClassName="font-heading text-2xl font-semibold tracking-tight leading-[1.1] text-zinc-800 sm:text-3xl"
            delay={0.3}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-600"
          >
            Answer customers instantly. Automate the paperwork. Win more deals with less effort.
            Business owners using AIS get hours back in week one — while competitors who wait fall further behind every month.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-book-demo-btn" onClick={() => navigate("/contact")} className="btn-primary">
              Book Your Free Demo Now <ArrowUpRight size={16} />
            </button>
            <button data-testid="hero-explore-btn" onClick={() => navigate("/products")} className="btn-secondary">
              See What AI Can Do <ArrowRight size={16} />
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }} className="mt-16 grid grid-cols-2 gap-px border border-zinc-900/10 bg-zinc-900/10 sm:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="bg-[#F7F8FA] p-6">
                <p className="font-heading text-4xl font-bold text-cyan-600">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={VERTICALS} />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-7xl px-6 py-32">
        <Reveal><SectionLabel>Why Act Now</SectionLabel></Reveal>
        <div className="mt-12 space-y-0 border-t border-zinc-900/10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.1}>
              <div data-testid={`manifesto-chapter-${m.num}`} className="grid grid-cols-1 gap-6 border-b border-zinc-900/10 py-12 md:grid-cols-12">
                <span className="font-mono text-sm text-cyan-600 md:col-span-2">/{m.num}</span>
                <h3 className="font-heading text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl md:col-span-4">{m.title}</h3>
                <p className="text-base leading-relaxed text-zinc-600 md:col-span-6">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PLATFORM PILLARS */}
      <section className="border-y border-zinc-900/10 bg-[#F0F2F5] py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>What's Under the Hood</SectionLabel>
            <h2 className="mt-6 max-w-2xl font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">
              Six building blocks. One job: growing your business.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600">
              You don't need to understand the technology — that's our job. Here's what's working for you behind the scenes.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px border border-zinc-900/10 bg-zinc-900/10 sm:grid-cols-2 lg:grid-cols-3">
            {ENGINES.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.06}>
                <div data-testid={`engine-card-${e.id}`} className="group h-full bg-[#F0F2F5] p-8 transition-colors duration-300 hover:bg-[#F5FBFC]">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-400">{e.code}</span>
                    <span className="h-2 w-2 bg-zinc-300 transition-colors duration-300 group-hover:bg-cyan-500" />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-zinc-900">{e.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{e.description}</p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-600/70">{e.spec}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <button data-testid="pillars-platform-link" onClick={() => navigate("/platform")} className="btn-secondary mt-12">
              See the Architecture <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-6 py-32">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionLabel>Where You'll Feel It First</SectionLabel>
              <h2 className="mt-6 font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">Real results, fast.</h2>
            </div>
            <button data-testid="featured-all-products-btn" onClick={() => navigate("/products")} className="btn-secondary">
              All 10 Products <ArrowRight size={16} />
            </button>
          </div>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
          {featured.map((p, i) => {
            const Icon = featuredIcons[i];
            return (
              <Reveal key={p.id} delay={i * 0.08}>
                <div data-testid={`featured-product-${p.id}`} className="card-hover group h-full border border-zinc-900/10 bg-[#FFFFFF] p-10">
                  <div className="flex items-start justify-between">
                    <Icon size={28} className="text-cyan-600" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-zinc-400">/{p.num}</span>
                  </div>
                  <h3 className="mt-8 font-heading text-2xl font-semibold text-zinc-900">{p.name}</h3>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-cyan-600">{p.tagline}</p>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-600">{p.description}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {p.verticals.map((v) => (
                      <span key={v} className="border border-zinc-900/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{v}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MANAGED SERVICES TEASER */}
      <section className="border-y border-zinc-900/10 bg-[#E9F4F6] py-28">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>New Division — Managed AI Services</SectionLabel>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
              <div>
                <h2 className="max-w-2xl font-heading text-4xl tracking-tight text-zinc-900 sm:text-5xl">
                  Don't just buy the software. <span className="text-cyan-600">Let us run the operation.</span>
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600">
                  AI agents, intelligent automation, and trained human specialists — combined into managed services that answer your calls, chase your leads, and clear your back-office work. You buy outcomes, not hours.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-px border border-zinc-900/10 bg-zinc-900/10 md:grid-cols-4">
            {["AI Customer Service", "Sales & Lead Support", "AI Voice Agents", "Managed AI Operations"].map((s, i) => (
              <Reveal key={s} delay={i * 0.05}>
                <div className="h-full bg-white p-6">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-400">MS-0{i + 1}</p>
                  <p className="mt-3 font-heading text-lg text-zinc-900">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="home-ms-explore-btn" onClick={() => navigate("/managed-services")} className="btn-primary">
                Explore Managed AI Services <ArrowRight size={16} />
              </button>
              <button data-testid="home-ms-assessment-btn" onClick={() => navigate("/assessment")} className="btn-secondary">
                Free AI Operations Assessment <ArrowUpRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BLOCKCHAIN TEASER */}
      <section className="border-y border-zinc-900/10 bg-[#E9F4F6]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 py-32 lg:grid-cols-2">
          <Reveal>
            <SectionLabel>Trust Layer</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">
              When AI decides, <span className="text-cyan-600">the chain remembers.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-600">
              Every answer your AI gives comes with receipts — what it looked at, when, and why. If a customer, partner, or auditor ever asks "can you prove that?", the answer is always yes. No other AI gives you that peace of mind.
            </p>
            <button data-testid="trust-layer-link" onClick={() => navigate("/platform")} className="btn-primary mt-10">
              How Verify Works <ArrowUpRight size={16} />
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden border border-zinc-900/10">
              <img src={IMAGES.blockchain} alt="Blockchain network visualization" className="aspect-[4/3] w-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#F7F8FA] to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 border-t border-zinc-900/10 bg-white/85 p-4 backdrop-blur-md">
                <p className="font-mono text-[10px] text-cyan-600">anchor_tx: 0x8f2a…e91c · block 21,847,203 · verified ✓</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="mx-auto max-w-7xl px-6 py-32">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden border border-zinc-900/10">
              <img src={IMAGES.socialProof} alt="Enterprise team" className="aspect-[4/3] w-full object-cover opacity-60 grayscale" />
              <div className="absolute inset-0 bg-cyan-500/5 mix-blend-overlay" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionLabel>Design Partners</SectionLabel>
            <blockquote className="mt-8 font-heading text-2xl font-medium leading-snug text-zinc-900 sm:text-3xl">
              "We stopped debating whether the AI was right. Now we just check the proof."
            </blockquote>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">— VP Compliance, Global Care Provider (Design Partner)</p>
            <div className="mt-12 grid grid-cols-3 gap-px border border-zinc-900/10 bg-zinc-900/10">
              {[["94%", "Faster document review"], ["3.2x", "Proposal throughput"], ["100%", "Audit trail coverage"]].map(([v, l]) => (
                <div key={l} className="bg-[#F7F8FA] p-5">
                  <p className="font-heading text-2xl font-bold text-cyan-600">{v}</p>
                  <p className="mt-1 text-[11px] leading-tight text-zinc-500">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="border-t border-zinc-900/10 py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <SectionLabel>Case Studies</SectionLabel>
            <h2 className="mt-6 max-w-2xl font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">Design partners, measurable outcomes.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {CASE_STUDIES.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div data-testid={`case-study-${c.num}`} className="card-hover flex h-full flex-col border border-zinc-900/10 bg-[#FFFFFF] p-8">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-cyan-600">/{c.num}</span>
                    <span className="border border-zinc-900/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.vertical}</span>
                  </div>
                  <h3 className="mt-8 font-heading text-xl font-semibold leading-snug text-zinc-900">{c.title}</h3>
                  <blockquote className="mt-5 border-l-2 border-cyan-500/50 pl-4 text-sm italic leading-relaxed text-zinc-600">"{c.quote}"</blockquote>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">— {c.author}, {c.partner}</p>
                  <div className="mt-auto pt-8">
                    <div className="grid grid-cols-3 gap-px border border-zinc-900/10 bg-zinc-900/10">
                      {c.metrics.map(([v, l]) => (
                        <div key={l} className="bg-[#FFFFFF] p-3">
                          <p className="font-heading text-lg font-bold text-cyan-600">{v}</p>
                          <p className="mt-1 text-[10px] leading-tight text-zinc-500">{l}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {c.products.map((p) => (
                        <span key={p} className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-600">{p}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL CTA */}
      <section className="border-t border-zinc-900/10 bg-[#F0F2F5] py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 lg:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col justify-between border border-cyan-500/30 bg-cyan-500/5 p-10">
              <div>
                <img src={LOGO_URL} alt="" className="h-14 w-14 object-cover" />
                <h3 className="mt-8 font-heading text-3xl font-semibold text-zinc-900">Every week you wait has a price.</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-600">Hours lost. Deals missed. Competitors pulling ahead. In one free 30-minute call, we'll show you exactly where AI saves your business time and money — this week, not next year.</p>
              </div>
              <button data-testid="cta-book-demo-btn" onClick={() => navigate("/contact")} className="btn-primary mt-10 w-fit">
                Claim Your Free Demo <ArrowUpRight size={16} />
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col justify-between border border-zinc-900/10 bg-[#FFFFFF] p-10">
              <div>
                <p className="label-mono">The Signal</p>
                <h3 className="mt-8 font-heading text-3xl font-semibold text-zinc-900">Not ready yet? Don't fall behind.</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-600">One short email a month: what AI is doing for businesses like yours, in plain English. Stay ahead of the curve — even before you're ready to jump.</p>
              </div>
              <div className="mt-10"><NewsletterForm testId="home-newsletter" /></div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
